import type { ReactNode } from 'react';
import { cx } from '../cx';
import { useIsDesktop } from '../useIsDesktop';

/**
 * THE PAGE TITLE IN THE CONTENT (redesign register L3, L7; brief FE-FOUNDATION section 5;
 * mockups `.page-head`: dashboard-v2-side.html:139-144, onboarding-v2.html:142-145,
 * clients-v2.html:113-115).
 *
 * On Ben's five pages the title stands large at the top of the content instead of in the
 * window toolbar (L3); every other screen keeps the toolbar title. The page pairs this with
 * `PageHeader`'s `largeTitle` option, which leaves the desktop toolbar without the title text
 * and keeps its accessible name, so a page has exactly one `h1`.
 *
 * DESKTOP ONLY. Phone views are their own round (P6): below the breakpoint the PageHeader's
 * own large title stays, and this renders nothing, so the phone never shows two titles.
 *
 * Title 28 (`--text-xl`, L7 "page title 28"); the line under it in the secondary label colour
 * at the body size (open question Q-CM-2 decided 15, the L7 body); an inline slot beside the
 * title for the data age (onboarding-v2.html:143) and a trailing slot for the page's one
 * button (onboarding-v2.html:144).
 */
export interface LargeTitleProps {
  readonly title: string;
  readonly subtitle?: ReactNode | undefined;
  /** Beside the title, on its baseline: the data age. */
  readonly inline?: ReactNode | undefined;
  /** At the trailing edge: the page's one button. */
  readonly trailing?: ReactNode | undefined;
  readonly className?: string | undefined;
}

export function LargeTitle({ title, subtitle, inline, trailing, className }: LargeTitleProps) {
  const desktop = useIsDesktop();
  if (!desktop) return null;
  return (
    <div className={cx('flex items-start gap-4', className)} data-large-title="">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-xl font-bold tracking-tight text-ink">{title}</h1>
          {inline !== undefined && <span className="text-2xs">{inline}</span>}
        </div>
        {subtitle !== undefined && <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>}
      </div>
      {trailing !== undefined && <div className="flex shrink-0 items-center">{trailing}</div>}
    </div>
  );
}
